import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { $getSelection, $setSelection, type BaseSelection } from 'lexical';

import { useRichTextEditor } from './editor';

type LinkDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

export const LinkDialog = ({ open, onClose }: LinkDialogProps) => {
  const { editor, commands } = useRichTextEditor();
  const [url, setUrl] = useState('');
  const savedSelection = useRef<BaseSelection | null>(null);

  // Save selection when dialog opens
  useEffect(() => {
    if (open && editor) {
      editor.getEditorState().read(() => {
        savedSelection.current = $getSelection()?.clone() ?? null;
      });
    }
  }, [open, editor]);

  const restoreSelection = useCallback(() => {
    if (!editor || !savedSelection.current) return;

    editor.update(() => {
      const selection = savedSelection.current;

      if (selection) {
        $setSelection(selection);
      }
    });
  }, [editor]);

  const handleClose = useCallback(() => {
    savedSelection.current = null;
    setUrl('');
    onClose();
  }, [onClose]);

  const handleInsert = useCallback(() => {
    const trimmed = url.trim();

    if (!trimmed) return;

    // Block dangerous schemes (javascript:, data:, vbscript:)
    const normalized = trimmed.toLowerCase().replace(/\s/g, '');

    if (/^(javascript|data|vbscript):/i.test(normalized)) return;

    // Prepend https:// if no scheme provided
    const finalUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    restoreSelection();
    commands.insertLink(finalUrl);
    handleClose();
  }, [url, commands, restoreSelection, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={linkDialogSx}>
      <DialogTitle>
        <Typography variant="text-md-semibold">Insert link</Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          id="rt-link-url-input"
          autoFocus
          fullWidth
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleInsert();
            }
          }}
          size="small"
          sx={linkInputSx}
        />
      </DialogContent>
      <DialogActions sx={linkActionsSx}>
        <Button id="rt-link-cancel-btn" hierarchy="Secondary gray" size="md" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          id="rt-link-insert-btn"
          hierarchy="Primary"
          size="md"
          onClick={handleInsert}
          disabled={!url.trim()}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const linkDialogSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    borderRadius: backrs.radius['radius-xl'],
  },
});

const linkInputSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-sm'],
});

const linkActionsSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-lg'],
  gap: backrs.spacing['spacing-sm'],
});
